import { readonly, shallowRef } from 'vue';
import type {
  ErpDefinitions,
  ErpFlatTexts,
  ErpForm,
  ErpLanguage,
  ErpMenuDefinition,
  ErpRouteTable,
} from './types';

/**
 * The ERP's screen definitions, held for the session (BLP/nx#3257). They are
 * global per release, not per user, so nothing clears them on logout. The
 * loader (`load.ts`) fills the store from the API; tests preload it from disk
 * (`testing/definitions.ts`).
 *
 * Every accessor is synchronous and throws before the definitions are in: a
 * consumer that reads too early fails loudly in dev and in tests instead of
 * rendering from an empty table. Reactive consumers (the command palette)
 * watch `erpDefinitionsLoaded`.
 */

let current: ErpDefinitions | null = null;
const loaded = shallowRef(false);

/** True once the definitions are in. */
export const erpDefinitionsLoaded = readonly(loaded);

/** Installs the definitions; a later call replaces them. */
export function setErpDefinitions(definitions: ErpDefinitions): void {
  current = definitions;
  loaded.value = true;
}

function loadedDefinitions(what: string): ErpDefinitions {
  if (!current) {
    throw new Error(
      `ERP definitions are not loaded yet: ${what} was read before loadErpDefinitions() finished`
    );
  }
  return current;
}

/** The release the definitions were served by. */
export function erpDefinitionsVersion(): string {
  return loadedDefinitions('the version').version;
}

/** Every form by key. */
export function erpForms(): Readonly<Record<string, ErpForm>> {
  return loadedDefinitions('the forms').forms;
}

/** One form; throws on an unknown key. */
export function erpForm(key: string): ErpForm {
  const form = loadedDefinitions(`form "${key}"`).forms[key];
  if (!form) throw new Error(`ERP definitions have no form "${key}"`);
  return form;
}

export function erpRouteTable(): ErpRouteTable {
  return loadedDefinitions('the route table').routes;
}

export function erpMenuDefinition(): ErpMenuDefinition {
  return loadedDefinitions('the menu').menu;
}

/** The flat texts of one language. */
export function erpDefinitionTexts(lang: ErpLanguage): ErpFlatTexts {
  return loadedDefinitions(`the ${lang} texts`).texts[lang];
}
